import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  DownloadSimple,
  MagnifyingGlass,
  Envelope,
  Buildings,
  UsersThree,
  CalendarBlank,
  Trash,
  ArrowUpRight,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "new", label: "New", tone: "bg-emerald-100 text-emerald-800" },
  { value: "contacted", label: "Contacted", tone: "bg-sky-100 text-sky-800" },
  { value: "converted", label: "Converted", tone: "bg-violet-100 text-violet-800" },
  { value: "archived", label: "Archived", tone: "bg-zinc-100 text-zinc-600" },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        (l.email || "").toLowerCase().includes(s) ||
        (l.company_name || "").toLowerCase().includes(s) ||
        (l.name || "").toLowerCase().includes(s) ||
        (l.source || "").toLowerCase().includes(s)
      );
    });
  }, [leads, q, statusFilter]);

  const stats = useMemo(() => {
    const s = { new: 0, contacted: 0, converted: 0, archived: 0 };
    leads.forEach((l) => { if (s[l.status] !== undefined) s[l.status] += 1; });
    return s;
  }, [leads]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("lom_token");
    // Use anchor with Authorization workaround: fetch blob then download.
    fetch(`${API_BASE}/leads/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "lom-timetrack-leads.csv";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Leads exported");
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10" data-testid="leads-page">
      <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Sales &middot; Marketing</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-zinc-950">
            Trial leads
          </h1>
          <p className="text-zinc-600 mt-2 max-w-xl">
            Every visitor who filled the &ldquo;Try free for 14 days&rdquo; form on the landing page.
            Reach out fast &mdash; conversion drops sharply after 24 hours.
          </p>
        </div>
        <button
          type="button"
          data-testid="leads-export-btn"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-300 hover:bg-zinc-50 text-zinc-950 text-sm font-semibold rounded-sm"
        >
          <DownloadSimple size={16} weight="bold" /> Export CSV
        </button>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="New" value={stats.new} tone="emerald" testid="stat-new" />
        <StatCard label="Contacted" value={stats.contacted} tone="sky" testid="stat-contacted" />
        <StatCard label="Converted" value={stats.converted} tone="violet" testid="stat-converted" />
        <StatCard label="Archived" value={stats.archived} tone="zinc" testid="stat-archived" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="leads-search-input"
            placeholder="Search email, company, name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:border-zinc-950"
          />
        </div>
        <select
          data-testid="leads-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-zinc-300 rounded-sm text-sm bg-white focus:outline-none focus:border-zinc-950"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <div className="text-xs text-zinc-500 ml-auto">
          Showing <span className="font-bold text-zinc-950">{filtered.length}</span> of {leads.length}
        </div>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] font-bold tracking-widest uppercase text-zinc-500">
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Team</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="leads-table-body">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <div className="text-zinc-500 mb-1">No leads {q || statusFilter !== "all" ? "match your filters" : "yet"}.</div>
                    {!q && statusFilter === "all" && (
                      <div className="text-xs text-zinc-400">
                        Share your landing page &mdash; leads will appear here automatically.
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filtered.map((l) => <LeadRow key={l.id} lead={l} onStatus={changeStatus} onDelete={remove} />)
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    violet: "bg-violet-50 border-violet-200 text-violet-900",
    zinc: "bg-zinc-50 border-zinc-200 text-zinc-900",
  }[tone] || "bg-zinc-50 border-zinc-200 text-zinc-900";
  return (
    <div className={`border rounded-sm p-4 ${bg}`} data-testid={testid}>
      <div className="text-[10px] font-bold tracking-widest uppercase opacity-70">{label}</div>
      <div className="font-heading font-black text-3xl tracking-tighter mt-1">{value}</div>
    </div>
  );
}

function LeadRow({ lead, onStatus, onDelete }) {
  const created = lead.created_at ? new Date(lead.created_at) : null;
  const dateStr = created ? created.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
  const timeStr = created ? created.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
  const statusMeta = STATUS_OPTIONS.find((s) => s.value === lead.status) || STATUS_OPTIONS[0];
  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`lead-row-${lead.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Envelope size={14} className="text-zinc-400" weight="bold" />
          <a href={`mailto:${lead.email}`} className="font-semibold text-zinc-950 hover:underline">
            {lead.email}
          </a>
        </div>
        {lead.name && <div className="text-xs text-zinc-500 ml-6 mt-0.5">{lead.name}</div>}
      </td>
      <td className="px-4 py-3">
        {lead.company_name ? (
          <div className="flex items-center gap-2 text-zinc-800">
            <Buildings size={14} className="text-zinc-400" weight="bold" /> {lead.company_name}
          </div>
        ) : <span className="text-zinc-400">—</span>}
      </td>
      <td className="px-4 py-3">
        {lead.team_size ? (
          <div className="inline-flex items-center gap-1 text-xs bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-sm">
            <UsersThree size={11} weight="bold" /> {lead.team_size}
          </div>
        ) : <span className="text-zinc-400">—</span>}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs font-mono text-zinc-500">{lead.source || "—"}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-zinc-700">
          <CalendarBlank size={13} className="text-zinc-400" weight="bold" />
          <span>{dateStr}</span>
          <span className="text-zinc-400 text-xs">{timeStr}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <select
          data-testid={`lead-status-${lead.id}`}
          value={lead.status}
          onChange={(e) => onStatus(lead.id, e.target.value)}
          className={`text-xs font-semibold px-2 py-1 rounded-sm border-0 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${statusMeta.tone}`}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <a
            href={`mailto:${lead.email}?subject=Welcome%20to%20LOM%20TimeTrack&body=Hi%20${encodeURIComponent(lead.name || "there")}%2C%0A%0AThanks%20for%20trying%20LOM%20TimeTrack!`}
            data-testid={`lead-email-${lead.id}`}
            title="Send email"
            className="p-1.5 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 rounded-sm"
          >
            <ArrowUpRight size={14} weight="bold" />
          </a>
          <button
            type="button"
            data-testid={`lead-delete-${lead.id}`}
            onClick={() => onDelete(lead.id)}
            title="Delete lead"
            className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm"
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </td>
    </tr>
  );
}
